
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { PERMISSION } from '@/permissions';
import { toErrorMessage } from '@/utils/errorHandling';
import Toast from '@/utils/toast';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TypeToConfirmModal from '@/components/shared/TypeToConfirmModal.vue';
import DatePicker from '@/components/shared/DatePicker.vue';

const namespace: string = 'auth';

@Component<EndOfSchoolYear>({
	components: {
		InfoBanner,
		DatePicker,
		TextButton,
		TypeToConfirmModal
	}
})
export default class EndOfSchoolYear extends Vue {
	@Prop({ type: String, required: false }) private date!: string|undefined;
	@Action('clearStudentsAndSubsidies', { namespace: 'subsidies' }) private clearStudentsAndSubsidies!: () => Promise<{ clearedCodes: number }>;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Getter('hasPermission', { namespace }) private hasPermission!: (permission: PERMISSION) => boolean;

	$refs!: { endSchoolEndDatePicker: DatePicker; };

	private PERMISSION = PERMISSION;
	private showModal: boolean = false;
	private showClearModal: boolean = false;
	private isClearing: boolean = false;
	private endSchoolYearDate: string = '';

	/**
	 * The minimum date that can be selected in the datepicker. Which is tomorrow, based on the restaurant's timezone.
	 *
	 * @return {string}
	 */
	private get minDate(): string {
		return DateTime.now().setZone(this.restaurant.menu_configuration?.zone || 'America/Toronto').plus({ day: 1 }).toFormat('yyyy-MM-dd');
	}

	/**
	 * Date picker should be disabled unless we're past the end of the school year date.
	 * For example if the end of school year is set to June 30th, the date picker should be disabled on July 1st.
	 *
	 * @return {boolean}
	 */
	private get isDateDisabled(): boolean {
		if (!this.date) {
			return false;
		}
		const endSchoolDateTime = DateTime.fromISO(this.date, { zone: this.restaurant.menu_configuration?.zone || 'America/Toronto' }).endOf('day');
		return endSchoolDateTime >= DateTime.now().endOf('day').setZone(this.restaurant.menu_configuration?.zone || 'America/Toronto');
	}

	private created(): void {
		this.endSchoolYearDate = this.date || '';
	}

	/**
	 * Open the confirmation modal.
	 * Close the date picker before to prevent browser crash
	 *
	 * @return {void}
	 */
	private openConfirmModal(): void {
		this.$refs.endSchoolEndDatePicker.menu = false;
		this.showModal = true;
	}

	/**
	 * Close the modal and reset the fields to their initial values.
	 *
	 * @return {void}
	 */
	private closeModal(): void {
		this.showModal = false;
		this.endSchoolYearDate = this.date || '';
		this.$refs.endSchoolEndDatePicker.date = this.endSchoolYearDate;
	}

	/**
	 * Update the end school year date after confirmation from the user and close the modal.
	 *
	 * @return {void}
	 */
	private updateEndSchoolYearDate(): void {
		this.$emit('input', this.endSchoolYearDate);
		this.showModal = false;
	}

	/**
	 * Close the clear confirmation modal.
	 *
	 * @return {void}
	 */
	private closeClearModal(): void {
		this.showClearModal = false;
	}

	/**
	 * Clear saved students and subsidies after confirmation, then notify the user.
	 *
	 * @return {Promise<void>}
	 */
	private async confirmClear(): Promise<void> {
		this.isClearing = true;
		try {
			const { clearedCodes } = await this.clearStudentsAndSubsidies();
			Toast.success(this.$t('settings.end_school_year.saved_students.success', { count: clearedCodes }));
			this.closeClearModal();
		}
		catch (error) {
			Toast.error(this.$t('settings.end_school_year.saved_students.error', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.isClearing = false;
		}
	}
}
